import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { EnvelopeSimple, Phone, MapPin, ArrowUpRight, PaperPlaneTilt } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import Seo from "@/components/Seo";

const SERVICE_OPTIONS = [
  "Brand Activation",
  "Event Technology",
  "IT & AV Rental",
  "Not sure yet",
];

export default function Contact() {
  const { settings } = useSettings();
  const [searchParams] = useSearchParams();
  const preselectedProduct = searchParams.get("product") || "";
  usePageTitle("contact");

  const email = settings?.contact_email || "hello@onetec.eu";
  const phone = settings?.contact_phone || "+32 2 702 36 36";
  const phoneHref = `tel:${phone.replace(/\s+/g, "")}`;
  const addressLines = (settings?.contact_address || "OneTec SRL\nTollaan 75\nB-1932 Zaventem").split("\n");
  const vat = settings?.vat || "BE0478.889.493";

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service_interest: "",
    message: preselectedProduct ? `I'm interested in: ${preselectedProduct}\n\n` : "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (preselectedProduct) {
      setForm((f) => ({ ...f, message: `I'm interested in: ${preselectedProduct}\n\n` }));
    }
  }, [preselectedProduct]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll reply within 24 hours.");
      setSent(true);
      setForm({ name: "", email: "", phone: "", company: "", service_interest: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or email hello@onetec.eu.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo title="Contact" path="/contact" description="Contact OneTec — Event technology, brand activations and IT/AV rental. Based in Zaventem, Belgium. Email hello@onetec.eu or call +32 2 702 36 36." />
      <section className="border-b-2 border-ot-fg">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <p className="text-xs uppercase tracking-widest font-bold text-ot-indigo">Contact</p>
          <h1 className="font-display text-6xl md:text-[9vw] uppercase tracking-tighter leading-[0.88] mt-4">
            Don't be shy.<br />
            <span className="bg-ot-peach border-2 border-ot-fg px-3">Say hello.</span>
          </h1>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 md:py-20 grid md:grid-cols-12 gap-10 md:gap-16">
        {/* Left: form */}
        <div className="md:col-span-7">
          {sent ? (
            <div className="nb-card p-10 md:p-14 text-center" data-testid="contact-success">
              <div className="w-16 h-16 mx-auto bg-ot-peach border-2 border-ot-fg flex items-center justify-center">
                <PaperPlaneTilt size={32} weight="bold" />
              </div>
              <h2 className="font-display text-3xl md:text-5xl uppercase mt-6 tracking-tighter">Message received.</h2>
              <p className="mt-3 text-lg text-ot-fg/80">We'll be back within 24 hours. Usually sooner.</p>
              <button onClick={() => setSent(false)} className="nb-btn mt-8" data-testid="send-another">
                Send another <ArrowUpRight weight="bold" />
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="nb-card p-6 md:p-10 space-y-5" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest block mb-2">Name *</label>
                  <input
                    className="nb-input"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Willis"
                    required
                    data-testid="contact-input-name"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest block mb-2">Email *</label>
                  <input
                    type="email"
                    className="nb-input"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="jane@company.com"
                    required
                    data-testid="contact-input-email"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest block mb-2">Phone</label>
                  <input
                    className="nb-input"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+32 …"
                    data-testid="contact-input-phone"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest block mb-2">Company</label>
                  <input
                    className="nb-input"
                    value={form.company}
                    onChange={update("company")}
                    placeholder="Company name"
                    data-testid="contact-input-company"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-widest block mb-2">Interested in</label>
                <div className="flex flex-wrap gap-2" data-testid="contact-service-chips">
                  {SERVICE_OPTIONS.map((s) => (
                    <button
                      type="button"
                      key={s}
                      onClick={() => setForm({ ...form, service_interest: s })}
                      data-testid={`service-chip-${s.replace(/\s+/g, "-").toLowerCase()}`}
                      className={`px-3 py-2 border-2 border-ot-fg font-display uppercase text-xs transition-all ${
                        form.service_interest === s ? "bg-ot-indigo text-white" : "bg-white hover:bg-ot-peach"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-widest block mb-2">Message *</label>
                <textarea
                  className="nb-input min-h-[160px] resize-y"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us about your event, dates, audience size..."
                  required
                  data-testid="contact-input-message"
                />
              </div>

              <button
                type="submit"
                className="nb-btn w-full md:w-auto disabled:opacity-60"
                disabled={submitting}
                data-testid="contact-submit"
              >
                {submitting ? "Sending…" : "Send message"} <ArrowUpRight weight="bold" />
              </button>
            </form>
          )}
        </div>

        {/* Right: info */}
        <div className="md:col-span-5">
          <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tighter leading-none">
            Or talk directly.
          </h2>
          <p className="mt-4 text-ot-fg/80">We answer the phone. We reply to emails. We still remember what email is for.</p>

          <div className="mt-8 space-y-5">
            <a href={`mailto:${email}`} className="nb-card p-5 flex items-center gap-4 group" data-testid="contact-email-card">
              <div className="w-12 h-12 bg-ot-peach border-2 border-ot-fg flex items-center justify-center shrink-0">
                <EnvelopeSimple size={22} weight="bold" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest font-bold text-ot-fg/50">Email</p>
                <p className="font-display text-xl uppercase break-all">{email}</p>
              </div>
              <ArrowUpRight weight="bold" className="ml-auto group-hover:rotate-45 transition-transform" />
            </a>
            <a href={phoneHref} className="nb-card p-5 flex items-center gap-4 group" data-testid="contact-phone-card">
              <div className="w-12 h-12 bg-ot-mint border-2 border-ot-fg flex items-center justify-center shrink-0">
                <Phone size={22} weight="bold" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest font-bold text-ot-fg/50">Phone</p>
                <p className="font-display text-xl uppercase">{phone}</p>
              </div>
              <ArrowUpRight weight="bold" className="ml-auto group-hover:rotate-45 transition-transform" />
            </a>
            <div className="nb-card p-5 flex items-start gap-4" data-testid="contact-address-card">
              <div className="w-12 h-12 bg-ot-coral border-2 border-ot-fg flex items-center justify-center shrink-0">
                <MapPin size={22} weight="bold" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest font-bold text-ot-fg/50">Address</p>
                <p className="font-display text-xl uppercase leading-tight mt-1">
                  {addressLines.map((l, i) => <span key={i}>{l}<br /></span>)}
                </p>
                <p className="text-xs text-ot-fg/60 mt-2">VAT {vat}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
